/**
 * Pure factories + selectors for the v2 dashboard config.
 *
 * Keeping these out of components and out of the state hook lets us
 * unit-test the data shape independently and reuse the same primitives
 * from the section editor (Fase 4) and drag & drop (Fase 5).
 *
 * **All operations are immutable** — every mutator returns a new
 * `DashboardConfigV2` so React renders pick up the change cleanly.
 */

import type {
  DashboardConfigV2,
  DashboardItem,
  ElementType,
  SectionConfig,
} from "@/types";
import { resolveLocalized } from "@pervasivemind/dashboard-render";
import { shortId } from "@/lib/utils";

// ── Defaults ──────────────────────────────────────────────

/** Empty v2 config used when a draft has no sections yet. */
export function emptyDashboard(): DashboardConfigV2 {
  return { version: 2, global_filters: [], sections: [] };
}

/** Default width applied when a new item lands in a section. */
const DEFAULT_WIDTH: Record<ElementType, number> = {
  kpi: 3,
  chart: 4,
  table: 12,
  text: 12,
};

/** Display-friendly label per element type (used in placeholders). */
export const ELEMENT_LABEL: Record<ElementType, string> = {
  kpi: "KPI",
  chart: "Chart",
  table: "Table",
  text: "Text",
};

// ── Section / item factories ──────────────────────────────

export function createSection(partial: Partial<SectionConfig> = {}): SectionConfig {
  return {
    id: partial.id ?? shortId("sec"),
    title: partial.title ?? "New section",
    subtitle: partial.subtitle ?? "",
    filters: partial.filters ?? [],
    items: partial.items ?? [],
    config: partial.config ?? {},
  };
}

export function createItem(
  element: ElementType,
  partial: Partial<DashboardItem> = {},
): DashboardItem {
  const width = clampWidth(partial.width ?? DEFAULT_WIDTH[element]);
  return {
    id: partial.id ?? shortId(element),
    element,
    width,
    props: partial.props ?? defaultProps(element),
  };
}

/** Sensible default props per element type — replaceable in the editor.
 *
 * ``label`` / ``title`` fields are intentionally empty so the editor's
 * ``useInitialFocus`` hook drops focus into them on mount, letting the
 * user type right away.
 */
function defaultProps(element: ElementType): Record<string, unknown> {
  switch (element) {
    case "kpi":
      return { label: "", sql: "", format: "number" };
    case "chart":
      return { type: "bar", title: "" };
    case "table":
      return {
        columns: [],
        default_sort: "",
        sort_dir: "desc",
        page_size: 50,
        density: "normal",
      };
    case "text":
      return { content: "" };
  }
}

// ── Section-list operations (immutable) ──────────────────

export function addSection(
  config: DashboardConfigV2,
  index: number,
  section: SectionConfig = createSection(),
): DashboardConfigV2 {
  const sections = [...config.sections];
  const safeIndex = Math.max(0, Math.min(index, sections.length));
  sections.splice(safeIndex, 0, section);
  return { ...config, sections };
}

export function removeSection(
  config: DashboardConfigV2,
  sectionId: string,
): DashboardConfigV2 {
  return {
    ...config,
    sections: config.sections.filter((s) => s.id !== sectionId),
  };
}

export function updateSection(
  config: DashboardConfigV2,
  sectionId: string,
  patch: Partial<SectionConfig>,
): DashboardConfigV2 {
  return {
    ...config,
    sections: config.sections.map((s) =>
      s.id === sectionId ? { ...s, ...patch } : s,
    ),
  };
}

export function moveSection(
  config: DashboardConfigV2,
  fromIndex: number,
  toIndex: number,
): DashboardConfigV2 {
  if (fromIndex === toIndex) return config;
  const sections = [...config.sections];
  const [moved] = sections.splice(fromIndex, 1);
  if (!moved) return config;
  sections.splice(Math.max(0, Math.min(toIndex, sections.length)), 0, moved);
  return { ...config, sections };
}

// ── Item-level operations (immutable) ────────────────────

export function addItem(
  config: DashboardConfigV2,
  sectionId: string,
  item: DashboardItem,
  index?: number,
): DashboardConfigV2 {
  return {
    ...config,
    sections: config.sections.map((s) => {
      if (s.id !== sectionId) return s;
      const items = [...s.items];
      const at = index ?? items.length;
      items.splice(Math.max(0, Math.min(at, items.length)), 0, item);
      return { ...s, items };
    }),
  };
}

export function removeItem(
  config: DashboardConfigV2,
  sectionId: string,
  itemId: string,
): DashboardConfigV2 {
  return {
    ...config,
    sections: config.sections.map((s) =>
      s.id === sectionId
        ? { ...s, items: s.items.filter((i) => i.id !== itemId) }
        : s,
    ),
  };
}

export function updateItem(
  config: DashboardConfigV2,
  sectionId: string,
  itemId: string,
  patch: Partial<DashboardItem>,
): DashboardConfigV2 {
  return {
    ...config,
    sections: config.sections.map((s) => {
      if (s.id !== sectionId) return s;
      return {
        ...s,
        items: s.items.map((i) => (i.id === itemId ? { ...i, ...patch } : i)),
      };
    }),
  };
}

export function setItemWidth(
  config: DashboardConfigV2,
  sectionId: string,
  itemId: string,
  width: number,
): DashboardConfigV2 {
  return updateItem(config, sectionId, itemId, { width: clampWidth(width) });
}

// ── Selectors / utilities ────────────────────────────────

export function findSection(
  config: DashboardConfigV2,
  sectionId: string | null,
): SectionConfig | null {
  if (!sectionId) return null;
  return config.sections.find((s) => s.id === sectionId) ?? null;
}

export function findItem(
  config: DashboardConfigV2,
  sectionId: string | null,
  itemId: string | null,
): DashboardItem | null {
  const section = findSection(config, sectionId);
  if (!section || !itemId) return null;
  return section.items.find((i) => i.id === itemId) ?? null;
}

/** Allowed widths for the resize selector, in ascending order. */
export const WIDTH_OPTIONS: readonly number[] = [3, 4, 6, 8, 12] as const;

export function clampWidth(width: number): number {
  if (!Number.isFinite(width)) return 12;
  if (width < 1) return 1;
  if (width > 12) return 12;
  return Math.round(width);
}

/** Title fallback used by SectionEditor / DiagramView if the user hasn't typed one.
 *
 * Iter 19: accept an optional ``locale`` so callers can render the section
 * title in the admin's active UI language. Defaults to ``"en"`` for
 * backwards-compat with non-locale-aware callers.
 */
export function sectionTitleFor(
  section: SectionConfig,
  fallback = "Section",
  locale: "en" | "es" | "pt" = "en",
): string {
  // Iter 16: ``section.title`` may be a bilingual LocalizedString —
  // coerce to plain string before ``.trim()``.
  const title = resolveLocalized(section.title, locale);
  return title.trim() || fallback;
}
